import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API, useAuth } from "../../App";
import { motion, AnimatePresence } from "framer-motion";
import {
  Package, Plus, Edit, Trash2, X, Search, Filter,
  ExternalLink, MapPin, DollarSign, Tag, BarChart3, Save
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const SOURCES = [
  { value: "facebook_marketplace", label: "Facebook Marketplace" },
  { value: "craigslist", label: "Craigslist" },
  { value: "direct_landlord", label: "Direct Landlord" },
  { value: "apartment_feed", label: "Apartment Feed" },
  { value: "other", label: "Other" },
];

const STATUSES = [
  { value: "new", label: "New", color: "bg-blue-100 text-blue-700" },
  { value: "reviewed", label: "Reviewed", color: "bg-amber-100 text-amber-700" },
  { value: "approved", label: "Approved", color: "bg-green-100 text-green-700" },
  { value: "sent", label: "Sent", color: "bg-purple-100 text-purple-700" },
  { value: "on_hold", label: "On Hold", color: "bg-slate-100 text-slate-700" },
  { value: "dead", label: "Dead", color: "bg-red-100 text-red-700" },
];

const TAGS = [
  { value: "best_value", label: "Best Value", color: "bg-green-500" },
  { value: "price_drop", label: "Price Drop", color: "bg-orange-500" },
  { value: "hidden_deal", label: "Hidden Deal", color: "bg-[#0A2342]" },
  { value: "move_in_special", label: "Move-In Special", color: "bg-amber-500" },
  { value: "luxury_find", label: "Luxury Find", color: "bg-purple-500" },
  { value: "no_deposit", label: "No Deposit", color: "bg-emerald-500" },
  { value: "pet_friendly", label: "Pet Friendly", color: "bg-blue-500" },
  { value: "top_rated", label: "Top Rated", color: "bg-red-500" },
];

const DEAL_TYPES = [
  { value: "on_market", label: "On Market", color: "bg-blue-100 text-blue-700" },
  { value: "off_market", label: "Off Market", color: "bg-violet-100 text-violet-700" },
];

const STRATEGIES = [
  { value: "", label: "None" },
  { value: "flip", label: "Fix & Flip" },
  { value: "buy_hold", label: "Buy & Hold" },
  { value: "value_add", label: "Value-Add" },
  { value: "turnkey", label: "Turnkey Rental" },
];

const emptyForm = {
  property_name: "", address: "", base_rent: "", fees: "0", bedrooms: "", bathrooms: "",
  area: "", source: "other", source_link: "", notes: "", special_offer: "",
  price_score: "", location_score: "", incentive_score: "", risk_score: "",
  marketing_tag: "", marketing_note: "",
  deal_type: "on_market", investment_strategy: "", estimated_rent_upside: "",
  occupancy_opportunity: "", investor_notes: "", investor_teaser: ""
};

const ScoreBadge = ({ score }) => {
  if (!score && score !== 0) return <span className="text-xs text-slate-400">—</span>;
  const color = score >= 80 ? "text-green-600 bg-green-50" : score >= 60 ? "text-amber-600 bg-amber-50" : "text-red-600 bg-red-50";
  return <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${color}`}>{score}</span>;
};

const DealInventory = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDealType, setFilterDealType] = useState("");
  const [searchQ, setSearchQ] = useState("");

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDeals = useCallback(async () => {
    try {
      let url = `${API}/deal-inventory?`;
      if (filterSource) url += `source=${filterSource}&`;
      if (filterStatus) url += `status=${filterStatus}&`;
      if (filterDealType) url += `deal_type=${filterDealType}`;
      const [dealsRes, statsRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/deal-inventory/stats/summary`, { headers })
      ]);
      setDeals(dealsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [headers, filterSource, filterStatus, filterDealType]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const filtered = deals.filter(d =>
    !searchQ || d.property_name?.toLowerCase().includes(searchQ.toLowerCase()) || d.area?.toLowerCase().includes(searchQ.toLowerCase())
  );

  const openCreate = () => { setForm({ ...emptyForm }); setEditId(null); setShowForm(true); };
  const openEdit = (deal) => {
    setForm({
      property_name: deal.property_name || "", address: deal.address || "",
      base_rent: deal.base_rent?.toString() || "", fees: deal.fees?.toString() || "0",
      bedrooms: deal.bedrooms || "", bathrooms: deal.bathrooms || "",
      area: deal.area || "", source: deal.source || "other",
      source_link: deal.source_link || "", notes: deal.notes || "",
      special_offer: deal.special_offer || "",
      price_score: deal.price_score?.toString() || "", location_score: deal.location_score?.toString() || "",
      incentive_score: deal.incentive_score?.toString() || "", risk_score: deal.risk_score?.toString() || "",
      marketing_tag: deal.marketing_tag || "", marketing_note: deal.marketing_note || "",
      deal_type: deal.deal_type || "on_market",
      investment_strategy: deal.investment_strategy || "",
      estimated_rent_upside: deal.estimated_rent_upside?.toString() || "",
      occupancy_opportunity: deal.occupancy_opportunity || "",
      investor_notes: deal.investor_notes || "",
      investor_teaser: deal.investor_teaser || ""
    });
    setEditId(deal.id);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      base_rent: parseFloat(form.base_rent) || 0,
      fees: parseFloat(form.fees) || 0,
      price_score: form.price_score ? parseFloat(form.price_score) : null,
      location_score: form.location_score ? parseFloat(form.location_score) : null,
      incentive_score: form.incentive_score ? parseFloat(form.incentive_score) : null,
      risk_score: form.risk_score ? parseFloat(form.risk_score) : null,
      estimated_rent_upside: form.estimated_rent_upside ? parseFloat(form.estimated_rent_upside) : null,
      investment_strategy: form.investment_strategy || null,
      occupancy_opportunity: form.occupancy_opportunity || null,
      investor_notes: form.investor_notes || null,
      investor_teaser: form.investor_teaser || null,
    };
    try {
      if (editId) {
        await axios.put(`${API}/deal-inventory/${editId}`, payload, { headers });
        toast({ title: "Deal updated" });
      } else {
        await axios.post(`${API}/deal-inventory`, payload, { headers });
        toast({ title: "Deal created" });
      }
      setShowForm(false);
      fetchDeals();
    } catch (err) {
      toast({ title: "Error", description: err.response?.data?.detail || "Failed", variant: "destructive" });
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this deal?")) return;
    try {
      await axios.delete(`${API}/deal-inventory/${id}`, { headers });
      toast({ title: "Deal deleted" });
      fetchDeals();
    } catch { toast({ title: "Failed to delete", variant: "destructive" }); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/deal-inventory/${id}`, { status: newStatus }, { headers });
      fetchDeals();
    } catch { toast({ title: "Failed to update status", variant: "destructive" }); }
  };

  const getStatusInfo = (s) => STATUSES.find(st => st.value === s) || STATUSES[0];
  const getTagInfo = (t) => TAGS.find(tg => tg.value === t);

  return (
    <div className="space-y-6" data-testid="deal-inventory-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0A2342] font-['Playfair_Display']" data-testid="deal-inventory-title">Deal Inventory</h1>
          <p className="text-slate-600">Source, score, and manage apartment deals</p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center" data-testid="add-deal-btn">
          <Plus className="w-5 h-5 mr-2" /> Add Deal
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-[#0A2342]">{stats.total}</p>
            <p className="text-xs text-slate-500">Total Deals</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{stats.by_status?.approved || 0}</p>
            <p className="text-xs text-slate-500">Approved</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-[#DE6E4B]">{stats.avg_score || 0}</p>
            <p className="text-xs text-slate-500">Avg Score</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{stats.on_market || 0}</p>
            <p className="text-xs text-slate-500">On Market</p>
          </div>
          <div className="bg-white rounded-xl border border-violet-200 p-4 text-center border-2">
            <p className="text-2xl font-bold text-violet-600">{stats.off_market || 0}</p>
            <p className="text-xs text-slate-500">Off-Market</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input type="text" placeholder="Search by name or area..." value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              className="input-field pl-10 text-sm" data-testid="deal-search" />
          </div>
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)}
            className="input-field w-full md:w-44 text-sm" data-testid="deal-filter-source">
            <option value="">All Sources</option>
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field w-full md:w-40 text-sm" data-testid="deal-filter-status">
            <option value="">All Statuses</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterDealType} onChange={(e) => setFilterDealType(e.target.value)}
            className="input-field w-full md:w-40 text-sm" data-testid="deal-filter-deal-type">
            <option value="">All Types</option>
            {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
      </div>

      {/* Deals List */}
      <div className="bg-white rounded-xl border border-slate-200" data-testid="deals-list">
        {loading ? (
          <div className="p-12 text-center"><span className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2342] inline-block" /></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No deals found</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(deal => {
              const statusInfo = getStatusInfo(deal.status);
              const tagInfo = getTagInfo(deal.marketing_tag);
              return (
                <div key={deal.id} className="p-4 md:p-5 hover:bg-slate-50 transition-colors" data-testid={`deal-row-${deal.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    {/* Score */}
                    <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                      <span className={`text-lg font-bold ${deal.overall_score >= 90 ? 'text-green-600' : deal.overall_score >= 75 ? 'text-emerald-600' : deal.overall_score >= 60 ? 'text-amber-600' : 'text-slate-400'}`}>
                        {deal.overall_score || "—"}
                      </span>
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-[#0A2342] truncate">{deal.property_name}</h3>
                        {tagInfo && (
                          <span className={`${tagInfo.color} text-white text-[10px] font-bold px-2 py-0.5 rounded-full`}>
                            {tagInfo.label}
                          </span>
                        )}
                        {deal.deal_type === "off_market" && (
                          <span className="bg-violet-100 text-violet-700 text-[10px] font-bold px-2 py-0.5 rounded-full" data-testid={`deal-offmarket-badge-${deal.id}`}>
                            Off-Market
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{deal.area}</span>
                        <span>{deal.bedrooms}{deal.bathrooms ? ` / ${deal.bathrooms}` : ""}</span>
                        <span>{SOURCES.find(s => s.value === deal.source)?.label || deal.source}</span>
                        {deal.source_link && <a href={deal.source_link} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline flex items-center gap-0.5"><ExternalLink className="w-3 h-3" />Link</a>}
                      </div>
                      {deal.marketing_note && <p className="text-xs text-slate-500 mt-1 italic">"{deal.marketing_note}"</p>}
                    </div>
                    {/* Price */}
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-[#DE6E4B]">${deal.total_monthly_cost?.toLocaleString()}<span className="text-xs text-slate-400 font-normal">/mo</span></p>
                      {deal.fees > 0 && <p className="text-[10px] text-slate-400">Rent ${deal.base_rent?.toLocaleString()} + Fees ${deal.fees?.toLocaleString()}</p>}
                      {deal.special_offer && <p className="text-[10px] text-green-600 font-medium mt-0.5">{deal.special_offer}</p>}
                    </div>
                    {/* Scores */}
                    <div className="flex gap-1 shrink-0">
                      {[{l:"P",v:deal.price_score},{l:"L",v:deal.location_score},{l:"I",v:deal.incentive_score},{l:"R",v:deal.risk_score}].map(s => (
                        <div key={s.l} className="text-center w-8" title={`${s.l === 'P' ? 'Price' : s.l === 'L' ? 'Location' : s.l === 'I' ? 'Incentive' : 'Risk'}: ${s.v || '—'}`}>
                          <p className="text-[9px] text-slate-400">{s.l}</p>
                          <ScoreBadge score={s.v} />
                        </div>
                      ))}
                    </div>
                    {/* Status + Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <select value={deal.status} onChange={(e) => handleStatusChange(deal.id, e.target.value)}
                        className={`text-xs border rounded-lg px-2 py-1 font-medium ${statusInfo.color}`}
                        data-testid={`deal-status-${deal.id}`}>
                        {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                      <button onClick={() => openEdit(deal)} className="p-1.5 text-slate-400 hover:text-blue-500" data-testid={`edit-deal-${deal.id}`}>
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(deal.id)} className="p-1.5 text-slate-400 hover:text-red-500" data-testid={`delete-deal-${deal.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4 pt-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
              className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl mb-8" data-testid="deal-form-modal">
              <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-2xl z-10">
                <h3 className="text-lg font-bold text-[#0A2342]">{editId ? "Edit Deal" : "Add New Deal"}</h3>
                <button onClick={() => setShowForm(false)} className="p-1 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
              <form onSubmit={handleSave} className="p-6 space-y-5">
                {/* Property Details */}
                <div>
                  <p className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Property Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2">
                      <input type="text" required placeholder="Property Name *" value={form.property_name} onChange={e => setForm({...form, property_name: e.target.value})}
                        className="input-field text-sm" data-testid="deal-form-name" />
                    </div>
                    <input type="text" placeholder="Address" value={form.address} onChange={e => setForm({...form, address: e.target.value})} className="input-field text-sm" />
                    <input type="text" required placeholder="Area *" value={form.area} onChange={e => setForm({...form, area: e.target.value})} className="input-field text-sm" data-testid="deal-form-area" />
                    <input type="text" required placeholder="Bedrooms * (e.g. 1 Bed)" value={form.bedrooms} onChange={e => setForm({...form, bedrooms: e.target.value})} className="input-field text-sm" data-testid="deal-form-bedrooms" />
                    <input type="text" placeholder="Bathrooms" value={form.bathrooms} onChange={e => setForm({...form, bathrooms: e.target.value})} className="input-field text-sm" />
                  </div>
                </div>
                {/* Pricing */}
                <div>
                  <p className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Monthly Cost</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Base Rent *</label>
                      <input type="number" required placeholder="1200" value={form.base_rent} onChange={e => setForm({...form, base_rent: e.target.value})}
                        className="input-field text-sm" data-testid="deal-form-rent" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Fees</label>
                      <input type="number" placeholder="0" value={form.fees} onChange={e => setForm({...form, fees: e.target.value})} className="input-field text-sm" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Total/mo (auto)</label>
                      <div className="input-field text-sm bg-slate-50 flex items-center text-[#DE6E4B] font-bold">
                        ${((parseFloat(form.base_rent) || 0) + (parseFloat(form.fees) || 0)).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <input type="text" placeholder="Special offer (e.g. First month free)" value={form.special_offer} onChange={e => setForm({...form, special_offer: e.target.value})} className="input-field text-sm mt-3" />
                </div>
                {/* Source */}
                <div>
                  <p className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2"><Filter className="w-4 h-4" /> Source</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="input-field text-sm" data-testid="deal-form-source">
                      {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <input type="url" placeholder="Listing URL" value={form.source_link} onChange={e => setForm({...form, source_link: e.target.value})} className="input-field text-sm" />
                  </div>
                  <textarea rows={2} placeholder="Notes..." value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input-field text-sm mt-3" />
                </div>
                {/* Scoring */}
                <div>
                  <p className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Deal Score (1-100)</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[{k:"price_score",l:"Price vs Market"},{k:"location_score",l:"Location"},{k:"incentive_score",l:"Incentives"},{k:"risk_score",l:"Risk (low=bad)"}].map(s => (
                      <div key={s.k}>
                        <label className="text-xs text-slate-500">{s.l}</label>
                        <input type="number" min="1" max="100" step="1" placeholder="—" value={form[s.k]}
                          onChange={e => setForm({...form, [s.k]: e.target.value})}
                          className="input-field text-sm" data-testid={`deal-form-${s.k}`} />
                      </div>
                    ))}
                  </div>
                  {(form.price_score || form.location_score || form.incentive_score || form.risk_score) && (
                    <p className="text-xs text-slate-500 mt-2">
                      Overall: <span className="font-bold text-[#0A2342]">
                        {(([form.price_score, form.location_score, form.incentive_score, form.risk_score].filter(Boolean).reduce((a,b) => a + parseFloat(b), 0)) / [form.price_score, form.location_score, form.incentive_score, form.risk_score].filter(Boolean).length).toFixed(1)}
                      </span>/100
                    </p>
                  )}
                </div>
                {/* Investor / Off-Market */}
                <div>
                  <p className="text-sm font-semibold text-violet-700 mb-3 flex items-center gap-2"><Package className="w-4 h-4" /> Investor / Off-Market</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-500">Deal Type</label>
                      <select value={form.deal_type} onChange={e => setForm({...form, deal_type: e.target.value})}
                        className="input-field text-sm" data-testid="deal-form-deal-type">
                        {DEAL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Investment Strategy</label>
                      <select value={form.investment_strategy} onChange={e => setForm({...form, investment_strategy: e.target.value})}
                        className="input-field text-sm" data-testid="deal-form-strategy">
                        {STRATEGIES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Est. Rent Upside %</label>
                      <input type="number" step="0.1" placeholder="e.g. 15" value={form.estimated_rent_upside}
                        onChange={e => setForm({...form, estimated_rent_upside: e.target.value})}
                        className="input-field text-sm" data-testid="deal-form-upside" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500">Occupancy Opportunity</label>
                      <select value={form.occupancy_opportunity} onChange={e => setForm({...form, occupancy_opportunity: e.target.value})}
                        className="input-field text-sm" data-testid="deal-form-occupancy">
                        <option value="">None</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>
                  </div>
                  <textarea rows={2} placeholder="Investor notes (internal only)..." value={form.investor_notes}
                    onChange={e => setForm({...form, investor_notes: e.target.value})} className="input-field text-sm mt-3" data-testid="deal-form-inv-notes" />
                  <input type="text" placeholder="Public teaser for gated view..." value={form.investor_teaser}
                    onChange={e => setForm({...form, investor_teaser: e.target.value})} className="input-field text-sm mt-3" data-testid="deal-form-inv-teaser" />
                </div>
                {/* Marketing */}
                <div>
                  <p className="text-sm font-semibold text-[#0A2342] mb-3 flex items-center gap-2"><Tag className="w-4 h-4" /> Marketing</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {TAGS.map(t => (
                      <button key={t.value} type="button" onClick={() => setForm({...form, marketing_tag: form.marketing_tag === t.value ? "" : t.value})}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${form.marketing_tag === t.value ? `${t.color} text-white shadow-md` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                        data-testid={`deal-form-tag-${t.value}`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <input type="text" placeholder="Why this is a good pick" value={form.marketing_note}
                    onChange={e => setForm({...form, marketing_note: e.target.value})} className="input-field text-sm" data-testid="deal-form-note" />
                </div>
                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 px-4 py-3 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 btn-primary flex items-center justify-center" data-testid="deal-form-save">
                    {saving ? <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <><Save className="w-4 h-4 mr-2" /> {editId ? "Update" : "Save"} Deal</>}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DealInventory;
